/* eslint-disable no-console */
import { Wrap, StyledButton as Button } from './styles';

import GbaProject from 'services/models/GbaProject';
import geoAlert from 'services/utils/alert';
import geoConfirm from 'services/utils/confirm';

import Icon from '@map/components/Icons';
import LinearProgress from '@material-ui/core/LinearProgress';
/**
 * useRef permite que persista valores entre renderizações, ele pode
 * ser usado para armazenar um valor mutável que não causa uma nova
 * renderização quando atualizado e pode ser usado para acessar di-
 * retamente um elemento do DOM.
 * 
 * useEffect é um hook que permite executar efeitos colaterais em
 * seus componentes, por exemplo: buscar dados, atualizar diretamente
 * o DOM e cronômetros, ele aceita dois argumentos o segundo é opcional.
 * 
 * useState é um hook que nos permite rastrear o estado em um
 * componente de função, o estado geralmente se refere a dados ou
 * propriedads que precisam ser rastreador em um aplicativo.
 */
import { useRef, useEffect, useState } from 'react';
/**
 *  useModules é responsavel por gerenciar os modulos do GeoBahia,
 *  todos os módulos criados na ADM.
 * 
 *  useMap é um hook personalizado criado para gerenciar a comunicação
 *  dos componentes com o mapa.
 */
import { useModule, useMap } from 'views/MapView/hooks';

/**
 * Função responsável por abertura do modal para exportar arquivos, onde
 * é possivel excluir camadas atuais do projeto para a configuação nao ficar
 * salva. 
 * 
 * @param {string} mapContext Context
 * @param {boolean} onClose Fechar o modal
 * @returns Retorna o carregamento do projeto importado e fecha
 * o modal depois.
 */
export const Export = ({ mapContext, onClose, ...props }) => {
  const iframeRef = useRef();

  const [loading, setLoading] = useState(false);
  const [gbaProject, setGbaProject] = useState(null);
  const [layers, setLayers] = useState(null);
  const [hasLayerHidden, setHasLayerHidden] = useState(false)
  const [hasLayerRaster, setHasLayerRaster] = useState(false)
  const moduleCtx = useModule();
  const mapCtx = useMap();

  // Replay da animação SVG
  useEffect(() => {
    let animationInterval;
    if (iframeRef.current && loading) {
      iframeRef.current.contentWindow.postMessage('play', '*');
      animationInterval = setInterval(
        () => iframeRef.current.contentWindow.postMessage('play', '*'),
        4000
      );
    }
    return () => {
      if (animationInterval) clearInterval(animationInterval);
    };
  }, [iframeRef, loading]);

  useEffect(() => {
    if (moduleCtx.activeModule) {
      setLoading(true);
      let exportProject = new GbaProject({ ...mapCtx, ...moduleCtx });
      //Verifica se existe camadas ocultas
      exportProject.layers.forEach((e) => { 
        if(e.visible === false) setHasLayerHidden(true);        
        if(e.dataType === 'Raster') setHasLayerRaster(true);
      })
      //Aplica filtro exportando apenas camadas visiveis e que NÃO são 'Raster'
      exportProject.layers = exportProject.layers.filter((layer) => layer.visible === true && layer.dataType !== 'Raster')
      exportProject.generateJson();
      setGbaProject(exportProject);
      setLayers(exportProject.layers);
      setLoading(false);
    }
  }, [mapCtx, moduleCtx]);

  // Verifica caso todas as camadas estejam ocultadas e manda um alert
  useEffect(() => {
    if(layers?.length < 1){
      onClose && onClose();
      geoAlert({
        title: 'GEOBAHIA Informa:',
        message: 'O projeto não pode ser exportado, pois todas as camadas estão ocultas e/ou são raster.',
        buttonText: 'OK',
      });
    }
  }, [layers, onClose])

  return (
    <Wrap>
      <div className="progress-area">
        <div className="progress-head">
          <h4>Exportando</h4>
          {!loading && <h4>100%</h4>}
        </div>
        <LinearProgress
          variant={loading ? 'indeterminate' : 'determinate'}
          value="100"
        />
      </div>
      <div className={`animated-svg ${loading && 'show-animation'}`}>
        <iframe
          ref={iframeRef}
          title="svg-animation"
          frameBorder="0"          
        ></iframe>
      </div>
      {!loading && (
        <div className="gba-data">
          {layers &&
            layers.map((l, i) => (
              <div className="layer" key={i}>
                <div className="title">
                  <p>{l.getTitle() || l.title}</p>
                  <span>{l.length && `(${l.length})`}</span>
                </div>
                <div className={`fake-progress ${l.error && 'error'}`}></div>
                <button
                  className="remove-button"
                  onClick={() => {
                    gbaProject.removeLayer(i);
                    gbaProject.generateJson();
                    setGbaProject(gbaProject);
                    setLayers(gbaProject.layers);
                  }}
                >
                  <Icon i="map-trash" width="10" height="11" />
                </button>
              </div>
            ))}
        </div>
      )}
      <div className="footer">
        <div className="button-area">
          {loading ? (
          <Button
            variant="contained"
            disabled
            onClick={() => {
              setLoading(true);
              gbaProject.exportToFile().then(() => {
                setLoading(false);
                onClose && onClose();
              });
            }}
          >
            Exportar
          </Button>
          ) : (
            <Button
            variant="contained"
            disabled={!gbaProject || layers.length < 1}
            onClick={() => {
              if(hasLayerHidden || hasLayerRaster){
                let typeLayerNotExport = 'ocultas';
                if (hasLayerRaster) typeLayerNotExport = 'do tipo raster';
                if (hasLayerHidden && hasLayerRaster) typeLayerNotExport = 'ocultas e do tipo raster';
                geoConfirm({
                  message:
                    'Camadas ' + typeLayerNotExport + ' não serão exportadas. Deseja continuar?',
                  callback: (confirmed) => {
                    if (confirmed) {
                      setLoading(true);
                      gbaProject.exportToFile().then(() => {
                        setLoading(false);
                        onClose && onClose();
                      });
                    }
                  },
                });              
              } else {
                setLoading(true);
                gbaProject.exportToFile().then(() => {
                  setLoading(false);
                  onClose && onClose();
                });
              }
            }}
          >
            Exportar
          </Button>
          )}
        </div>
      </div>
    </Wrap>
  );
};

export default Export;
